// The skin profile: what SkinGenie knows about the person's skin, and the one
// place that builds, stores and sends it.
//
// This used to live inside face-analysis-result.svelte, where the questions
// that fill it sat below the primary button. Most people never scrolled that
// far, so the engine ran on defaults: age 25, skin type guessed from the
// detected concerns, and no declared sensitivities at all. Age decides whether
// an eye cream gets a slot, and sensitivities are a safety filter, so those
// defaults were not harmless. The questions are their own step now, and both
// screens read and write the profile through here.

import { get } from 'svelte/store';
import { userStore } from './store.js';
import { formDataStore } from './components/store';

export const CONCERN_OPTIONS = [
    'acne',
    'dry skin',
    'dull skin',
    'face redness',
    'hyperpigmentation',
    'large pores',
    'oily skin',
    'rough texture',
    'under eye circles',
    'under eye wrinkles',
    'wrinkles'
];

// Common skincare sensitivities. The recommendation engine excludes products
// containing these ingredients.
//
// Fragrance and essential oils are DELIBERATELY ABSENT. Measured against the
// production catalogue on 2026-09-09: there are zero ingredient rows matching
// fragrance / parfum / perfume / limonene / linalool, so those options
// excluded 29 and 1 products out of 10,224. Offering a checkbox that does
// nothing is worse than offering none, because people with a fragrance
// allergy would trust it. Put them back once the scraper captures INCI
// allergen entries and the coverage is re-measured.
export const SENSITIVITY_OPTIONS = [
    { value: 'retinol', label: 'Retinoids' },
    { value: 'acids', label: 'Exfoliating acids (AHA / BHA / PHA)' },
    { value: 'benzoyl peroxide', label: 'Benzoyl peroxide' },
    { value: 'vitamin c', label: 'Vitamin C' },
    { value: 'niacinamide', label: 'Niacinamide' },
    { value: 'sulfates', label: 'Sulfates' },
    { value: 'parabens', label: 'Parabens' },
    { value: 'alcohol', label: 'Drying alcohols' },
    { value: 'nuts', label: 'Nut oils' },
    { value: 'lanolin', label: 'Lanolin' }
];

// Climate changes what a routine should include: a mist in dry air, an extra
// cleanse in humid air. The engine has always branched on it and nobody was
// ever asked -- `climate` was hardcoded to "dry" at serve.py:60 and the ZIP to
// 78681, so every user in the world was given the Round Rock, Texas routine.
//
// "temperate" is the honest default: CLIMATE_PRODUCT_TYPES has entries for
// humid and dry only, so anything else adds no step rather than inventing one.
// Measured across routine styles, skin types and concern counts: climate
// changes the slot list in 3 of 36 combinations, always a maximalist routine
// with one concern, and always the same mist. "humid" never changes anything,
// because the cleanser it would add is already in every routine. So nobody is
// asked for it yet -- a question that changes nothing costs more attention
// than it buys. What matters is that the server stops asserting an answer.
export const DEFAULT_CLIMATE = 'temperate';

export function safeParse(json, fallback) {
    try {
        const parsed = JSON.parse(json);
        return Array.isArray(parsed) ? parsed : fallback;
    } catch (error) {
        console.error('Could not parse stored value:', error);
        return fallback;
    }
}

// What the analyzer found: [{ concern, score }], strongest first.
export function storedFindings() {
    return safeParse(localStorage.getItem('AoI') || '[]', [])
        .filter(item => item && item.concern);
}

export function inferSkinType(concerns) {
    const hasDrySkin = concerns.includes('dry skin');
    const hasOilySkin = concerns.includes('oily skin');
    if (hasDrySkin && hasOilySkin) return 'combination';
    if (hasDrySkin) return 'dry';
    if (hasOilySkin) return 'oily';
    return 'normal';
}

export function ensureUserId() {
    const storeUser = get(userStore);
    if (storeUser.userId) return storeUser.userId;

    const existingUserId = localStorage.getItem('UserId');
    if (existingUserId) {
        userStore.setUserId(existingUserId);
        return existingUserId;
    }

    const generatedUserId = crypto.randomUUID
        ? crypto.randomUUID()
        : `anonymous-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    userStore.setUserId(generatedUserId);
    return generatedUserId;
}

// The profile as it stands, with the analysis filling in anything the person
// has not answered yet.
export function loadProfile() {
    let saved = {};
    try {
        saved = JSON.parse(localStorage.getItem('skinProfile') || '{}') || {};
    } catch (error) {
        saved = {};
    }

    const findings = storedFindings();
    const detected = findings.map(item => item.concern);
    const concerns = Array.isArray(saved.areasOfImprovement) && saved.areasOfImprovement.length
        ? saved.areasOfImprovement
        : detected;

    return {
        areasOfImprovement: concerns,
        skinType: saved.skinType || inferSkinType(concerns),
        // No default age. 25 was the old one, and it silently built a
        // 25-year-old's routine for everybody who never scrolled to the field.
        age: typeof saved.age === 'number' ? saved.age : null,
        skinAllergies: safeParse(localStorage.getItem('skinAllergies') || 'null', null)
            || (Array.isArray(saved.skinAllergies) ? saved.skinAllergies : []),
        routineType: saved.routineType || 'mid_tier',
        climate: saved.climate || DEFAULT_CLIMATE,
        answered: !!saved.answered,
        userId: saved.userId || get(userStore).userId || localStorage.getItem('UserId')
    };
}

// Only concerns with a real reading are sent. A missing one defaults to full
// weight server-side, so a partial analysis degrades rather than silently
// deprioritising itself.
export function severityFor(concerns) {
    const findings = storedFindings();
    const out = {};
    for (const concern of concerns) {
        const match = findings.find(item => item.concern === concern);
        if (match && typeof match.score === 'number' && !Number.isNaN(match.score)) {
            out[concern] = match.score;
        }
    }
    return out;
}

export function buildProfilePayload(answers) {
    const concerns = answers.areasOfImprovement || [];
    return {
        areasOfImprovement: concerns,
        // How strongly the analyzer saw each concern, 0..1. It orders the
        // routine's slots and weights the ranking.
        concernSeverity: severityFor(concerns),
        skinType: answers.skinType || inferSkinType(concerns),
        // Always an array. This used to be the hardcoded string 'None' for
        // every user, while the app recommended retinoids and acids.
        skinAllergies: answers.skinAllergies || [],
        avoidIngredients: answers.skinAllergies || [],
        // Null until the person answers. Nothing stores a guessed age any
        // more; the request helpers substitute a value at send time so the
        // engine still gets a number.
        age: typeof answers.age === 'number' ? answers.age : null,
        // The ZIP was hardcoded to 78681 and read by nothing: there is no
        // zipcode anywhere in the engine. Sending a made-up one bought
        // nothing and looked like a real answer. Climate is the thing that
        // was actually wanted, and now it is asked.
        climate: answers.climate || DEFAULT_CLIMATE,
        routineType: answers.routineType || 'mid_tier',
        // Whether the person has actually been through the questions, so a
        // screen can tell an answer from a fallback.
        answered: !!answers.answered,
        userId: ensureUserId()
    };
}

export function persistProfile(answers) {
    const profile = buildProfilePayload(answers);
    formDataStore.set(profile);
    localStorage.setItem('skinProfile', JSON.stringify(profile));
    localStorage.setItem('skinAllergies', JSON.stringify(profile.skinAllergies));
    // Carry the analyzer's score through, so nothing downstream has to guess
    // which concern mattered most.
    localStorage.setItem('AoI', JSON.stringify(profile.areasOfImprovement.map(concern => ({
        concern,
        score: severityFor([concern])[concern] ?? null
    }))));
    return profile;
}

// The engine needs a number for age, and it decides whether an eye cream gets
// a slot. Until the person tells us, send the middle of the catalogue's range
// rather than storing a made-up age on their profile.
export const ASSUMED_AGE = 25;

export function engineProfile(profile) {
    return { ...profile, age: profile.age || ASSUMED_AGE };
}

export async function saveProfileToServer(answers) {
    const profile = persistProfile(answers);
    const response = await fetch('/api/save_user_data', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profile)
    });
    if (!response.ok) {
        throw new Error('Failed to save skin profile');
    }
    return profile;
}
